// Only route and endpoint names from this list may appear in telemetry. A
// client slug, name, email, ID, or an unreviewed new route becomes :id.
const SAFE_PATH_SEGMENTS = new Set([
  'en',
  'api',
  'auth',
  'authentication',
  'login',
  'logout',
  'finish-login',
  'finish-logout',
  'support',
  'admin',
  'member',
  'onboarding',
  'dashboard',
  'exam',
  'list',
  'detail',
  'book',
  'reschedule',
  'notifications',
  'health-history',
  'form',
  'settings',
  'advocate',
  'menu',
  'appointment-confirmed',
  'already-exists-appointment',
  'select-appointment',
  'privacy-protection',
  'all-secure',
  'personal-information',
  'verification',
  'forms',
  'all-set',
  'email-mismatch',
  'client',
  'calendar',
  'events',
  'create',
  'locations',
  'location',
  'active-events',
  'members-report',
  'appointments-report',
  'orgs-management',
  'org-directory',
  'countries',
  'products',
  'support-users',
  'report-users',
  'site-managers',
  'reporting',
  'examiners',
  'profiles',
  'add',
  'new',
  'appointment',
  'appointmentHold',
  'tempMember',
  'tempMemberAppointment',
  'memberAppointment',
  'client_location',
  'client_location_event',
  'resource',
  'report',
  'getReports',
  'getAvailableTimeSlot',
  'getActiveEventDatesByLocation',
  'getFirstAvailableTimeByLocation',
  'overlappedEvent',
  'overlappedRoomEvent',
  'byLocationId',
  'deleteAppointmentHold',
]);

const SAFE_ERROR_NAMES = new Set([
  'AbortError',
  'NetworkError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'TimeoutError',
  'TypeError',
]);

export function classifyTelemetryError(value: unknown): string {
  if (typeof value !== 'object' || value === null || !('name' in value)) {
    return 'Other';
  }
  const name = value.name;
  return typeof name === 'string' && SAFE_ERROR_NAMES.has(name)
    ? name
    : 'Other';
}

/** Retain useful route/endpoint context without query, hash, or dynamic values. */
export function sanitizeTelemetryUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return 'https://redacted.invalid/';
    }

    const path = url.pathname
      .split('/')
      .map((segment) => {
        if (!segment) return '';
        let decoded: string;
        try {
          decoded = decodeURIComponent(segment);
        } catch {
          return ':id';
        }
        return SAFE_PATH_SEGMENTS.has(decoded) ? decoded : ':id';
      })
      .join('/');

    return `${url.origin}${path || '/'}`;
  } catch {
    return 'https://redacted.invalid/';
  }
}

interface TelemetryRequest {
  reqId: string;
  url: string;
  method: string;
  headers?: unknown;
  body?: unknown;
  referrer?: unknown;
}

interface TelemetryResponse {
  reqId: string;
  method: string;
  status?: number;
  headers?: unknown;
  body?: unknown;
  url?: unknown;
}

export function sanitizeTelemetryRequest(request: TelemetryRequest) {
  return {
    reqId: request.reqId,
    url: sanitizeTelemetryUrl(request.url),
    method: request.method,
    headers: {},
    body: null,
  };
}

export function sanitizeTelemetryResponse(response: TelemetryResponse) {
  return {
    reqId: response.reqId,
    method: response.method,
    status: response.status,
    headers: {},
    body: null,
  };
}

/** Preserve layout and interaction replay while masking patient-facing content. */
export const LOGROCKET_PRIVACY_OPTIONS = {
  browser: { urlSanitizer: sanitizeTelemetryUrl },
  dom: {
    isEnabled: true,
    inputSanitizer: true,
    textSanitizer: true,
    imageSanitizer: true,
    disablePageTitles: true,
    hiddenAttributes: [
      'href',
      'src',
      'action',
      'value',
      'data-href',
      'data-name',
      'data-props',
      'data-event-id',
      'data-day-id',
      'data-start-date-time',
      'data-testid',
    ] as string[],
  },
  console: { isEnabled: false, shouldAggregateConsoleErrors: false },
  shouldCaptureIP: false,
  shouldDetectExceptions: false,
} as const;
